import api from '@/services/httpClient';

export type AnalyticsConsentState = 'granted' | 'denied' | 'unknown';

export const ANALYTICS_CONSENT_KEY = 'plf_analytics_consent';
export const ANALYTICS_POLICY_VERSION_KEY = 'plf_analytics_policy_version';
export const ANALYTICS_POLICY_VERSION = '2026-02-23';

const LEGACY_CONSENT_KEYS = ['analytics_consent', 'cookie_consent_analytics'] as const;
const ANALYTICS_VISITOR_KEY = 'plf_visitor_id';
const ANALYTICS_SESSION_KEY = 'plf_session_state';
const ANALYTICS_SESSION_COOKIE = 'plf_session_id';

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'granted', 'accepted', 'on']);
const FALSE_VALUES = new Set(['0', 'false', 'no', 'denied', 'off', 'rejected']);

type ConsentSource = 'banner' | 'settings' | 'registration' | string;

type SetConsentOptions = {
  source?: ConsentSource;
  policyVersion?: string;
  visitorId?: string | null;
};

const normalizeConsentValue = (raw: string): AnalyticsConsentState => {
  const value = raw.trim().toLowerCase();
  if (!value) {
    return 'unknown';
  }

  if (value.startsWith('{') || value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'boolean') {
        return parsed ? 'granted' : 'denied';
      }
      if (typeof parsed === 'string') {
        return normalizeConsentValue(parsed);
      }
    } catch (_err) {
      return 'unknown';
    }
  }

  if (TRUE_VALUES.has(value)) {
    return 'granted';
  }
  if (FALSE_VALUES.has(value)) {
    return 'denied';
  }
  return 'unknown';
};

const readLocalStorage = (key: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return localStorage.getItem(key);
  } catch (_err) {
    return null;
  }
};

const writeLocalStorage = (key: string, value: string): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch (_err) {
    // best effort
  }
};

const removeLocalStorage = (key: string): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(key);
  } catch (_err) {
    // best effort
  }
};

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const removeCookie = (name: string): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }
  const isSecure = window.location.protocol === 'https:';
  const sameSite = isSecure ? 'None' : 'Lax';
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=${sameSite}${
    isSecure ? ';Secure' : ''
  }`;
};

const resolveVisitorId = (explicitVisitorId?: string | null): string | null => {
  const direct = String(explicitVisitorId || '').trim();
  if (direct) {
    return direct;
  }
  return readLocalStorage(ANALYTICS_VISITOR_KEY) || getCookie(ANALYTICS_VISITOR_KEY);
};

const postConsent = (state: Exclude<AnalyticsConsentState, 'unknown'>, options?: SetConsentOptions): void => {
  const visitorId = resolveVisitorId(options?.visitorId);
  const payload = {
    state,
    policy_version: options?.policyVersion || ANALYTICS_POLICY_VERSION,
    visitor_id: visitorId || undefined,
    source: (options?.source || 'banner').toString(),
  };

  void api.post('/analytics/consent/', payload).catch((err) => {
    console.warn('Failed to post analytics consent:', err);
  });
};

export const readConsent = (): AnalyticsConsentState => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  const allKeys = [ANALYTICS_CONSENT_KEY, ...LEGACY_CONSENT_KEYS];
  for (const key of allKeys) {
    const raw = readLocalStorage(key);
    if (raw == null) {
      continue;
    }
    const normalized = normalizeConsentValue(raw);
    if (normalized !== 'unknown') {
      return normalized;
    }
  }
  return 'unknown';
};

export const readConsentPolicyVersion = (): string => {
  return readLocalStorage(ANALYTICS_POLICY_VERSION_KEY) || ANALYTICS_POLICY_VERSION;
};

export const hasConsent = (): boolean => readConsent() === 'granted';

export const setConsent = (
  state: Exclude<AnalyticsConsentState, 'unknown'>,
  options: SetConsentOptions = {},
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  writeLocalStorage(ANALYTICS_CONSENT_KEY, state);
  for (const key of LEGACY_CONSENT_KEYS) {
    writeLocalStorage(key, state);
  }
  writeLocalStorage(ANALYTICS_POLICY_VERSION_KEY, options.policyVersion || ANALYTICS_POLICY_VERSION);

  postConsent(state, options);

  window.dispatchEvent(
    new CustomEvent('analytics-consent-changed', {
      detail: {
        state,
        source: options.source || 'banner',
        policy_version: options.policyVersion || ANALYTICS_POLICY_VERSION,
      },
    }),
  );
};

export const withdrawConsent = (): void => {
  removeLocalStorage(ANALYTICS_VISITOR_KEY);
  removeLocalStorage(ANALYTICS_SESSION_KEY);
  removeCookie(ANALYTICS_VISITOR_KEY);
  removeCookie(ANALYTICS_SESSION_COOKIE);
};

// Backward-compatible aliases.
export const readAnalyticsConsent = readConsent;
export const hasAnalyticsConsent = hasConsent;
export const setAnalyticsConsent = (
  state: Exclude<AnalyticsConsentState, 'unknown'>,
  options?: SetConsentOptions,
): void => setConsent(state, options);
